'use client'

import React, { useEffect, useState } from 'react'
import { useLanguage } from '@/app/(frontend)/providers/LanguageProvider'
import { motion } from 'framer-motion'

type Testimonial = {
  id: string
  quote: string
  authorName: string
  role: string
  authorPhoto?: { thumbnailURL: string }
  companyLogo?: { thumbnailURL: string }
}

const ClientTestimonialsSlider = () => {
  const { language } = useLanguage()
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [currentSlide, setCurrentSlide] = useState(0)

  const isRTL = language === 'ar'

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const res = await fetch(`/api/testimonials?locale=${language}`)
        const json = await res.json()
        setTestimonials(json.docs)
      } catch (err) {
        console.error('Error fetching testimonials:', err)
      }
    }

    fetchTestimonials()
  }, [language])

  // ✅ Autoplay - only addition
  useEffect(() => {
    if (testimonials.length === 0) return

    const interval = setInterval(() => {
      setCurrentSlide((prev) =>
        prev === testimonials.length - 1 ? 0 : prev + 1
      )
    }, 5000)

    return () => clearInterval(interval)
  }, [testimonials])

  const goToSlide = (index: number) => {
    setCurrentSlide(index)
  }

  const getTransform = () => {
    return isRTL
      ? `translateX(${currentSlide * 100}%)`
      : `translateX(-${currentSlide * 100}%)`
  }

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        delay: 0.3 + i * 0.2,
      },
    }),
  }

  if (testimonials.length === 0) {
    return (
      <div className="text-white bg-black py-10 text-center">
        <p>
          No testimonials found for language: <strong>{language}</strong>
        </p>
      </div>
    )
  }

  const t = testimonials[currentSlide] ?? null
  if (!t) return null

  return (
    <section className="bg-black text-white px-4 py-24 2xl:px-[4.5rem] flex flex-col justify-center">
      {/* Header */}
      <div
        className={`max-w-4xl mx-auto mb-16 ${
          language === 'ar' ? 'text-right md:text-center' : 'text-left md:text-center'
        }`}
      >
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          viewport={{ once: true }}
          className="why-heading text-[1.5rem] md:text-[32px] xl:text-[38px] 2xl:text-[42px] font-[500] mb-6 lg:mb-8 xl:mb-10"
        >
          {language === 'ar' ? 'ماذا يقول عملاؤنا' : 'What Our Clients Say'}
        </motion.h2>

        <motion.p
          variants={fadeInUp}
          custom={1}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-[#f8f8f8] text-[15px] xl:text-base 2xl:text-lg max-w-[450px] mx-auto !leading-[1.3rem] lg:mb-2 xl:mb-4"
        >
          {language === 'ar'
            ? 'آراء حقيقية من الشركات والأفراد الذين يثقون بـ Container City® لتلبية احتياجاتهم التخزينية.'
            : 'Real feedback from businesses and individuals who trust Container City® with their storage needs'}
        </motion.p>
      </div>

      {/* Slider */}
      <div
        className="relative w-full mx-auto md:max-w-2xl lg:max-w-5xl xl:max-w-[1250px] 2xl:max-w-[1650px] lg:px-12 xl:px-0"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-700 ease-in-out"
            style={{ transform: getTransform() }}
          >
            {testimonials.map((t) => (
              <div key={t.id} className="w-full flex-shrink-0">
                <div className="flex flex-col lg:flex-row gap-4 xl:gap-8 2xl:gap-6 items-stretch xl:min-h-[500px]">
                  {/* Quote */}
                  <motion.div
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className={`w-full lg:w-[40%] bg-zinc-900 p-4 md:p-8 flex flex-col justify-start items-start lg:items-center ${
                      language === 'ar'
                        ? 'text-right lg:text-center'
                        : 'text-left lg:text-center'
                    }`}
                  >
                    <div
                      className={`w-full mb-4 ${
                        language === 'ar' ? 'text-right' : 'text-left'
                      } lg:text-center`}
                    >
                      <img
                        src="/images/quote.png"
                        alt="Quote Icon"
                        className="w-20 h-20 inline-block"
                      />
                    </div>

                    <p className="text-white text-[13px] md:text-xs xl:text-lg leading-relaxed font-light max-w-md mt-2 md:mt-10">
                      {t.quote}
                    </p>
                  </motion.div>

                  {/* Image & Logo */}
                  <div className="w-full lg:w-[60%] flex flex-col justify-between">
                    <div className="flex flex-col sm:flex-row gap-6 h-full flex-1">
                      {t.authorPhoto?.thumbnailURL && (
                        <motion.div
                          variants={fadeInUp}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true }}
                          className="flex-1 flex justify-center"
                        >
                          <img
                            src={t.authorPhoto.thumbnailURL}
                            alt={t.authorName}
                            className="w-full aspect-square object-cover"
                          />
                        </motion.div>
                      )}
                      {t.companyLogo?.thumbnailURL && (
                        <motion.div
                          variants={fadeInUp}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true }}
                          className="flex-1 flex justify-center bg-white items-center"
                        >
                          <img
                            src={t.companyLogo.thumbnailURL}
                            alt="Company Logo"
                            className="w-full aspect-square object-cover"
                          />
                        </motion.div>
                      )}
                    </div>

                    <motion.div
                      variants={fadeInUp}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      className="text-center mt-4 2xl:mt-6 py-6 bg-zinc-900"
                    >
                      <h3 className="text-xl font-semibold text-white">{t.authorName}</h3>
                      <p className="text-gray-400 text-[15px] md:text-sm">{t.role}</p>
                    </motion.div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center mt-6 lg:mt-8 xl:mt-6 gap-x-2">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 w-2 xl:w-2 xl:h-2 rounded-full transition-all duration-300 ${
                currentSlide === index
                  ? 'bg-white scale-125'
                  : 'bg-white/40 hover:bg-white/60 hover:scale-110'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export default ClientTestimonialsSlider
